import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { styled } from '@mui/material/styles';
import Button from '@components/Button';
import { GoogleNativeDarkIcon } from 'assets/images/icons';
import { getGoogleOAuthSignInUrl, getGoogleOAuthSignUpUrl } from '@constants/integrations';
import PostHogManager from '@widgets/posthog-manager';
import { ProductEvent } from '@constants/product-events';
import GTManager, { buildUserParams } from '@widgets/gtm';
import { authActions } from '@actions';
import { authService } from '@services';
import { handleApiError } from '@helpers/api';
import { useQueryParams } from '@hooks';
import { setUserInLocalStorage } from '@helpers/auth';
import { getPartnerCode } from '@helpers/partnerLink';

const StyledButton = styled(Button)({
    backgroundColor: '#4285F4',
    marginBottom: '16px',
    minWidth: 'fit-content',
    overflow: 'hidden',
    '&:hover': {
        backgroundColor: '#2472f2',
    },
    '& .MuiButton-startIcon': {
        display: 'block',
    },
});

const ButtonIconWrapper = styled('div')({
    backgroundColor: '#fff',
    height: 30,
    width: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderBottomLeftRadius: 7,
    borderTopLeftRadius: 7,
    position: 'absolute',
    left: 1,
});

const GOOGLE_ERROR = {
    CANCEL: 'access_denied',
};

const GoogleButtonAuth = ({ text, path, onClickCallback, language, action, onUserNotFound }) => {
    const query = useQueryParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        const code = query.get('code');
        const error = query.get('error');
        if (error === GOOGLE_ERROR.CANCEL) {
            //
        } else if (code) {
            signInWithGoogle(() =>
                authService.loginWithGoogle({
                    language,
                    code,
                    action,
                    redirectUri: `${window.location.origin}/${path}`,
                    // eslint-disable-next-line no-useless-escape
                    hubspotToken: document.cookie.replace(/(?:(?:^|.*;\s*)hubspotutk\s*\=\s*([^;]*).*$)|^.*$/, '$1'),
                    pageUrl: `${window.location.origin}/${path}`,
                    partnerCode: getPartnerCode(),
                }),
            );
        }
    }, [query]);

    const signInWithGoogle = async callback => {
        onClickCallback(true);
        try {
            const user = await callback();
            setUserInLocalStorage(user);
            GTManager.update(user);
            if (user.isNewUser) {
                GTManager.setRegistrationType('google');
                GTManager.push('user-registered', {
                    ...buildUserParams(user),
                    registration_type: 'google',
                });
            }
            PostHogManager.identify(user);
            if (user.isNewUser) {
                PostHogManager.capture(ProductEvent.UserRegistered, { registration_type: 'google' });
            }
            dispatch(authActions.setUser(user));
        } catch (error) {
            if (error?.errorCode === 'user_not_found' && onUserNotFound) {
                onUserNotFound();
            } else {
                handleApiError(error);
            }
        }
        onClickCallback(false);
        navigate(`/${path}`, { replace: true });
    };

    const onClick = () => (window.location = path === 'signup' ? getGoogleOAuthSignUpUrl() : getGoogleOAuthSignInUrl());

    return (
        <StyledButton
            id={`pl-button-google-${path === 'signup' ? 'signup' : 'signin'}`}
            type="button"
            variant="contained"
            fullWidth
            onClick={onClick}
        >
            <ButtonIconWrapper>
                <GoogleNativeDarkIcon />
            </ButtonIconWrapper>
            {text}
        </StyledButton>
    );
};

export default GoogleButtonAuth;
